import { memo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';

import { type SortByOption as TagsSortByOption } from '@air/api/types';

import { PrivatePopularSmartTags } from '~/components/Filters/SmartTagsFilter/PrivatePopularSmartTags';
import { SmartTagsSelect, type SmartTagsSelectProps } from '~/components/SmartTagSelect/SmartTagsSelect';
import {
  convertSmartTagToSelectOption,
  type SmartTagBase,
  type SmartTagSelectOption,
} from '~/components/SmartTagSelect/utils';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { useGetPrivateSmartTagList } from '~/swr-hooks/filters/useGetPrivateSmartTagList';

export type PrivateSmartTagsSelectProps = {
  selectedSmartTags?: SmartTagBase[];
  onSelectedSmartTagsChanged: (newSmartTags: SmartTagBase[]) => void;
  canCreateNewTags?: boolean;
  sortBy?: TagsSortByOption;
  className?: string;
} & Omit<SmartTagsSelectProps, 'onInputChange' | 'smartTags' | 'selectedSmartTags' | 'onSmartTagsSelectionChange'>;

export const PrivateSmartTagsSelect = memo(
  ({ selectedSmartTags = [], onSelectedSmartTagsChanged, className, ...props }: PrivateSmartTagsSelectProps) => {
    const [searchValue, setSearchValue] = useState('');
    const board = useSelector(centralizedBoardSelector);
    const libraryId = useURLLibraryIdSelector();

    const [debouncedSearchValue] = useDebounce(searchValue, 200, {
      trailing: true,
    });
    const { currentWorkspace } = useCurrentWorkspace();

    const { data = [], isLoading } = useGetPrivateSmartTagList({
      search: debouncedSearchValue,
      boardId: board?.id,
      libraryId: libraryId ?? board?.library?.id,
      workspaceId: currentWorkspace?.id,
    });

    const onSelectionChange = useCallback(
      (values: SmartTagSelectOption[]) => {
        const baseSmartTags = values.map(({ value, label }) => ({ id: value, label }));
        onSelectedSmartTagsChanged(baseSmartTags);
      },
      [onSelectedSmartTagsChanged],
    );

    const onSmartTagClick = useCallback(
      (smartTag: string) => {
        onSelectedSmartTagsChanged([{ id: smartTag, label: smartTag }]);
      },
      [onSelectedSmartTagsChanged],
    );

    return (
      <div className={className}>
        <SmartTagsSelect
          smartTags={data.map((smartTag) => ({ id: smartTag, label: smartTag }))}
          isLoading={isLoading}
          selectedSmartTags={selectedSmartTags.map((smartTag) => convertSmartTagToSelectOption(smartTag))}
          onSmartTagsSelectionChange={onSelectionChange}
          onInputChange={setSearchValue}
          {...props}
        />
        {!selectedSmartTags.length && <PrivatePopularSmartTags className="mt-3" onSmartTagClick={onSmartTagClick} />}
      </div>
    );
  },
);

PrivateSmartTagsSelect.displayName = 'PrivateSmartTagsSelect';
